import { envelopeData } from '../../../core/network/envelope';

// Lectura TOLERANTE del publicLiveState del evento (0106; contrato Q-320
// pendiente). Normaliza el sobre → { event, offerings[] }. Compartido por el
// dashboard interactivo y la pantalla de proyección.

const num = (v) => (v == null || v === '' ? 0 : Number(v) || 0);

// Bloque countdown del estado del evento (contrato market): { armed,
// countdown_at, countdown_seconds, opens_at, server_time }. Llega dentro de
// `state.countdown` (snapshot `market_event:state` y GET dashboard) o como
// payload propio del broadcast `market_event:countdown`. Devuelve el bloque
// crudo o null; la corrección de skew y el paso a ms los hace el consumidor
// (necesita el instante de recepción del server_time).
export function readCountdown(body) {
    const data = envelopeData(body);
    return data.countdown ?? null;
}

// Normaliza el bloque countdown a reloj LOCAL (ms) corrigiendo el skew con
// server_time (server_time recibido "fresco" restado a Date.now() = offset con
// el reloj del server; las horas del server se pasan a local sumando ese skew).
// Devuelve null (sin bloque), { armed:false } (agendado/desarmado) o
// { armed:true, countdownAt, opensAt, seconds }. Debe llamarse al RECIBIR el
// bloque (no en cada render) para capturar el skew con el server_time fresco.
export function normalizeCountdown(block) {
    if (!block) return null;
    if (!block.armed) return { armed: false };
    const st = block.server_time ? Date.parse(block.server_time) : NaN;
    const skew = Number.isNaN(st) ? 0 : Date.now() - st;
    const toLocal = (s) => { const t = s ? Date.parse(s) : NaN; return Number.isNaN(t) ? null : t + skew; };
    return {
        armed: true,
        countdownAt: toLocal(block.countdown_at),
        opensAt: toLocal(block.opens_at),
        seconds: block.countdown_seconds ?? null,
    };
}

export function readEventState(body) {
    const data = envelopeData(body);
    // El estado del market viene PLANO: event_id/name/slug/state/countdown/
    // offerings en la raíz (no anidado bajo `event`). Fallback a `data` para leer
    // name/state; se mantienen `event`/`market_event` por si algún día se anida.
    const event = data.event ?? data.market_event ?? data;
    const raw = data.offerings ?? data.items ?? data.cows ?? [];
    const offerings = raw.map((o) => {
        const offered = num(o.offered);
        const sold = num(o.sold ?? o.taken);
        const free = o.free ?? o.available ?? Math.max(0, offered - sold);
        const buyers = o.buyers ?? o.participants ?? [];
        return {
            id: o.offering_id ?? o.id,
            name: o.animal_label ?? o.name ?? o.animal?.name ?? o.number ?? (o.animal_id != null ? `Vaca #${o.animal_id}` : 'Vaca'),
            number: o.number ?? o.animal?.number ?? null,
            register: o.register ?? o.animal?.register ?? null,
            offered,
            sold,
            free: num(free),
            price: o.price_per_part ?? o.price ?? null,
            state: o.state ?? event.state ?? null,
            participantsCount: num(o.participants_count),
            buyers: buyers.map((b) => ({ name: b.display_name ?? b.name ?? b.first_name ?? '—', percent: num(b.percent) })),
        };
    });
    // Vaca en foco marcada por el subastador (POST /current). Viaja en el snapshot
    // (state y GET dashboard). null si el subastador no la fijó todavía.
    const currentOfferingId = data.current_offering_id ?? event.current_offering_id ?? null;
    return { event, offerings, currentOfferingId };
}

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

// Vaca EN FOCO: la que el subastador fijó (current_offering_id). Si no la fijó (o
// ya no está en la lista), fallback a la oferta abierta más "caliente" (mayor
// fracción colocada), o la primera. Compartida por el panel de puja y la pantalla.
export function pickFocal(offerings, currentId) {
    if (!offerings.length) return null;
    if (currentId != null) {
        const cur = offerings.find((o) => sameId(o.id, currentId));
        if (cur) return cur;
    }
    const open = offerings.filter((o) => o.state === 'open' && o.offered > 0);
    const pool = open.length ? open : offerings;
    return pool.reduce((best, o) =>
        (o.sold / (o.offered || 1)) > (best.sold / (best.offered || 1)) ? o : best, pool[0]);
}

// Ranking agregado del EVENTO: % total por comprador sumado sobre todas las
// vacas. Ordenado desc. (Solo nombre de pila — el dashboard público no trae PII.)
export function aggregateBuyers(offerings) {
    const map = new Map();
    offerings.forEach((o) => o.buyers.forEach((b) => {
        map.set(b.name, (map.get(b.name) || 0) + b.percent);
    }));
    return [...map.entries()]
        .map(([name, pct]) => ({ name, pct }))
        .sort((a, b) => b.pct - a.pct);
}
